import React, { useState } from 'react';
import { XMarkIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { ReportService } from '../../../shared/services/reportService';
import { FILE_FORMAT_OPTIONS } from '../config/reportsConfig';

function triggerDownload(base64, mimeType, filename) {
  const byteChars = atob(base64);
  const byteArray = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([byteArray], { type: mimeType }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const FORMAT_META = {
  CSV:   { desc: 'Comma-separated, opens in Excel / Google Sheets', bg: 'bg-sky-500' },
  EXCEL: { desc: 'Native Excel workbook (.xlsx)',                    bg: 'bg-emerald-500' },
  PDF:   { desc: 'Formatted, print-ready PDF document',             bg: 'bg-rose-500' },
};

export default function DownloadFormatModal({ reportId, onClose, onError }) {
  const [selected, setSelected] = useState('CSV');
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await ReportService.download(reportId, selected);
      const { fileName, mimeType, content } = res.data.data;
      triggerDownload(content, mimeType, fileName);
      onClose();
    } catch (err) {
      console.error('[ReportDownload] failed:', err);
      onError?.('Failed to download report.');
      onClose();
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#4057fe] flex items-center justify-center">
              <ArrowDownTrayIcon className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Download Report</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Format picker */}
        <div className="p-5 space-y-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">Choose the file format for your download.</p>
          <div className="space-y-2">
            {FILE_FORMAT_OPTIONS.map((opt) => {
              const meta = FORMAT_META[opt.value];
              const isSelected = selected === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => setSelected(opt.value)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                    isSelected
                      ? 'border-[#4057fe] bg-[#4057fe]/5 dark:bg-[#4057fe]/10'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0 ${meta.bg}`}>
                    {opt.label}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{opt.label}</div>
                    <div className="text-xs text-gray-400 dark:text-gray-500">{meta.desc}</div>
                  </div>
                  {isSelected && (
                    <div className="w-4 h-4 rounded-full bg-[#4057fe] flex items-center justify-center flex-shrink-0">
                      <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 10" fill="none">
                        <path d="M2 5l2.5 2.5L8 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-5 pb-5">
          <button
            onClick={onClose}
            className="text-sm px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="flex items-center gap-2 text-sm px-4 py-2 rounded-lg bg-[#4057fe] text-white hover:bg-[#3347e0] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {downloading
              ? <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              : <ArrowDownTrayIcon className="w-3.5 h-3.5" />
            }
            {downloading ? 'Downloading…' : 'Download'}
          </button>
        </div>
      </div>
    </div>
  );
}
